import { List } from "@mui/material";
import React from "react";
import { isMobile, isTablet } from "react-device-detect";
import { useLocation } from "react-router";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { NewViewTypeEnum } from "../../common/enums/NewViewTypeEnum";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { getDeviceRoute, getGroupRoute, getTagRoute, getTankBatteryRoute, getTankRoute, getViewIDFromPath, getWellRoute } from "../../common/utilities/RouteHelpers";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import useNavigateTo, { NavigateToOptions } from "../../hooks/use-navigate-to/useNavigateTo";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useBreadcrumb } from "../../queries/breadcrumb/UseBreadcrumb";
import { useEvaluateFeature } from "../../queries/features/UseEvaluateFeature";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { IRoute } from "../../routes/routes";
import { FeatureConstants } from "../../services/features-data-service/Models";
import { INavigationItem } from "../../services/navigation-data-service/Models";
import { IUICustomizationSettingsDTO } from "../../services/ui-customization-data-service/Models";
import { bindUICustomizationSettingsDialog } from "../ui-customization-settings-dialog/bindUICustomizationSettingsDialog";
import UICustomizationSettingsDialog from "../ui-customization-settings-dialog/UICustomizationSettingsDialog";
import { useUICustomizationSettingsDialogState } from "../ui-customization-settings-dialog/useUICustomizationSettingsDialogState";
import NavigationViewGroupedListSection from "./NavigationViewGroupedListSection";
import NavigationViewListSection from "./NavigationViewListSection";
import { getCustomizationViewCategorySettings } from "../../common/utilities/GetCustomizationViewCategorySettings";

export type NavigationViewListProps = {
    open: boolean;
    customizationSettings: IUICustomizationSettingsDTO | undefined;
    onSelect?: () => void;
};

export default function NavigationViewList({
    open,
    customizationSettings,
    onSelect
}: NavigationViewListProps) {

    const isPhone = isMobile && !isTablet;
    const location = useLocation();
    const routeContext = useRouteContext();
    const navigateTo = useNavigateTo();
    const [offlineState] = useOfflineDetectContext();
    const [userState] = useUserContext();
    const [viewAll, setViewAll] = React.useState<boolean>(false);
    const viewID = getViewIDFromPath(location.pathname, UIViewEnum.Group_SummaryListing_Devices);
    const configurationDialog = useUICustomizationSettingsDialogState();

    const { data } = useQueryNotifications(useBreadcrumb)({
        customerID: userState.customer?.id as number,
        viewID: viewID,
        routeContext: routeContext,
        offlineDataLoaded: offlineState.offlineDataLoaded,
        offline: offlineState.offline
    });

    const { data: navigationV2Feature } = useQueryNotifications(useEvaluateFeature)({
        feature: FeatureConstants.NavigationV2,
    });

    const navigationV2 = (navigationV2Feature ?? false);

    // const {
    //     data: customizationSettings
    // } = useQueryNotifications(useUICustomizationSettings)({
    //     userID: userState.user?.id as number,
    //     enabled: navigationV2
    // });

    function onClick(navigationItem: INavigationItem, route: IRoute, shiftKey: boolean, ctrlKey: boolean) {

        let url: string | undefined;

        let options: NavigateToOptions | undefined;

        if (shiftKey || ctrlKey) {
            options = {
                newViewType: shiftKey ? NewViewTypeEnum.NewWindow : NewViewTypeEnum.NewTab
            }
        }

        switch (navigationItem.objectTypeID) {
            case eLynxObjectTypeEnum.Group:
                url = getGroupRoute(route.viewID ?? UIViewEnum.Group_SummaryListing, navigationItem.objectID);
                break;

            case eLynxObjectTypeEnum.Device:

                //const viewID = route.viewID === UIViewEnum.Device_ManualEntry && !isPhone ? UIViewEnum.Device_GaugeSheet : UIViewEnum.Device_ManualEntry;

                // navigate to the 
                url = getDeviceRoute(route.viewID ?? UIViewEnum.Device_Details, routeContext.groupID, routeContext.wellID, navigationItem.objectID, isPhone);
                //url = getDeviceRoute(viewID, routeContext.groupID, routeContext.wellID, navigationItem.objectID);

                break;

            case eLynxObjectTypeEnum.Well:
                url = getWellRoute(route.viewID ?? UIViewEnum.Well_Details, routeContext.groupID, navigationItem.objectID);
                break;

            case eLynxObjectTypeEnum.Tank:
                url = getTankRoute(route.viewID ?? UIViewEnum.Tank_Details, routeContext.groupID, routeContext.deviceID, routeContext.tankBatteryID, navigationItem.objectID);
                break;

            case eLynxObjectTypeEnum.TankBattery:
                url = getTankBatteryRoute(route.viewID ?? UIViewEnum.TankBattery_Details, routeContext.groupID, navigationItem.objectID);
                break;

            case eLynxObjectTypeEnum.Tag:
                url = getTagRoute(route.viewID ?? UIViewEnum.Tag_Details, routeContext.groupID, routeContext.wellID, routeContext.deviceID, routeContext.tankBatteryID, routeContext.tankID, navigationItem.objectID);
                break;

            default:
                throw new Error(`Unable to navigate to object type: ${navigationItem.objectTypeID.toString()}`);
        }

        if (onSelect) {
            onSelect();
        }

        navigateTo(url, options);
    }

    const navigationItems = data?.breadcrumbItems
        .filter(item =>
            open
                // This will enable all groups in the breadcrumb to be displayed in the nav drawer
                //? true
                // This will enable only the current group to be displayed in the nav drawer
                ? item.objectTypeID !== eLynxObjectTypeEnum.Group || item.objectID === routeContext.groupID
                : item.objectTypeID === routeContext.contextObject?.objectType && item.objectID === routeContext.contextObject.id

        )
        .reverse();

    return (
        <>
            <List
                sx={{
                    paddingTop: 0,
                    overflowX: "hidden",
                    overflowY: "auto",
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1 1 auto'
                }}
            >
                {navigationV2
                    ? navigationItems?.map((item, index) => {

                        const settings = getCustomizationViewCategorySettings(
                            item.objectTypeID,
                            customizationSettings
                        );

                        return (open
                            ? <NavigationViewGroupedListSection key={index}
                                //open={open} 
                                navigationItem={item}
                                customizationSettings={settings}
                                viewAll={viewAll}
                                showActions={index == 0}
                                onClick={onClick}
                                onConfigureClick={() => {
                                    configurationDialog.open({
                                        userID: userState.user?.id as number
                                    });
                                }}
                                onViewAllClick={() => setViewAll(prev => !prev)}
                            />
                            : <NavigationViewListSection key={index}
                                open={open}
                                navigationItem={item}
                                customizationSettings={settings}
                                viewAll={viewAll}
                                onClick={onClick}
                            />
                        )
                    })
                    : navigationItems?.map((item, index) => {

                        const settings = getCustomizationViewCategorySettings(
                            item.objectTypeID,
                            customizationSettings
                        );

                        return (<NavigationViewListSection key={index}
                            open={open}
                            navigationItem={item}
                            customizationSettings={settings}
                            viewAll={viewAll}
                            onClick={onClick}
                        />)
                    })
                }
            </List>

            {/* Don't even bother rendering the dialog if navigationV2 is not enabled */}
            {navigationV2 &&
                <UICustomizationSettingsDialog {...bindUICustomizationSettingsDialog(configurationDialog)} />
            }
        </>
    );
}

